import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill, type StatusTone } from '@/components/px/StatusPill'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { selectProjectMembers } from '@/features/projects/slices/projectDetailSlice'
import type { ProjectMemberRow } from '@/features/projects/types'
import { useAppDispatch, useAppSelector } from '@/store'
import { MemberSelect } from './MemberSelect'
import {
  assignDifferenceOwner,
  disposeDifference,
  loadReconciliation,
  proposeDifference,
  selectMigrationMutating,
  selectMigrationNonce,
  selectReconciliationError,
  selectReconciliationLoading,
  selectReconciliationReport,
  toDisplayError
} from '../slices/migrationSlice'
import type { FlaggedDifferenceStatus, ReconciliationRow, ReconciliationRowStatus } from '../types'
import { DIFF_STATUS_TONE } from './tones'

const ROW_TONE: Record<ReconciliationRowStatus, StatusTone> = { MATCH: 'done', DIFFERENCE: 'review' }

const DISPOSITION_OPTIONS: readonly FlaggedDifferenceStatus[] = ['ACCEPTED', 'FIXED', 'RERUN_REQUIRED']

type ActiveAction = { diffId: string; verb: 'assign-owner' | 'propose' | 'dispose' } | null

/** One flagged difference's three distinct affordances (INV-5.6). */
function DifferenceActions({
  orgId,
  projectId,
  planId,
  runId,
  row,
  active,
  onActiveChanged,
  mutating,
  scopedError,
  members
}: {
  orgId: string
  projectId: string
  planId: string
  runId: string
  row: ReconciliationRow
  active: ActiveAction
  onActiveChanged: (next: ActiveAction) => void
  mutating: boolean
  scopedError: string | undefined
  members: readonly ProjectMemberRow[]
}) {
  const { t } = useTranslation('projectWorkspace')
  const dispatch = useAppDispatch()
  const difference = row.flaggedDifference
  const [ownerId, setOwnerId] = useState('')
  const [proposedStatus, setProposedStatus] = useState<FlaggedDifferenceStatus>('ACCEPTED')
  const [rationale, setRationale] = useState('')

  if (difference === undefined) return null
  // A plain nullable string (not `active` itself) so every check below is a
  // simple comparison — no null-narrowing of `active` needed at any site.
  const activeVerb = active !== null && active.diffId === difference.diffId ? active.verb : null

  const onAssignOwnerSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    void dispatch(
      assignDifferenceOwner({ orgId, projectId, planId, runId, diffId: difference.diffId, input: { ownerId } })
    )
    onActiveChanged(null)
  }

  const onProposeSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    void dispatch(
      proposeDifference({
        orgId,
        projectId,
        planId,
        runId,
        diffId: difference.diffId,
        input: { proposedStatus, rationale }
      })
    )
    onActiveChanged(null)
  }

  const onDisposeSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    void dispatch(
      disposeDifference({
        orgId,
        projectId,
        planId,
        runId,
        diffId: difference.diffId,
        input: { status: proposedStatus, rationale }
      })
    )
    onActiveChanged(null)
  }

  // Scope addition (Agon, 2026-09-23, item 4): a disposed (terminal)
  // difference offers no assign/propose/dispose act — the server refuses
  // them anyway; hiding them here is UX, not the real gate.
  const disposed = difference.disposed

  return (
    <div className="border-t border-border bg-muted/30 px-4 py-2.5" data-testid={`diff-actions-${difference.diffId}`}>
      {!disposed && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={mutating}
            onClick={() => onActiveChanged(activeVerb === 'assign-owner' ? null : { diffId: difference.diffId, verb: 'assign-owner' })}
          >
            {t('migration.reconciliation.assignOwner')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={mutating}
            onClick={() => onActiveChanged(activeVerb === 'propose' ? null : { diffId: difference.diffId, verb: 'propose' })}
          >
            {t('migration.reconciliation.propose')}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={mutating}
            onClick={() => onActiveChanged(activeVerb === 'dispose' ? null : { diffId: difference.diffId, verb: 'dispose' })}
          >
            {t('migration.reconciliation.dispose')}
          </Button>
          {difference.ownerName !== undefined && (
            <span className="text-xs text-muted-foreground">
              {t('migration.reconciliation.ownedBy', { name: difference.ownerName })}
            </span>
          )}
        </div>
      )}

      {disposed && difference.ownerName !== undefined && (
        <span className="text-xs text-muted-foreground">
          {t('migration.reconciliation.ownedBy', { name: difference.ownerName })}
        </span>
      )}

      {/* Scope addition items 1–2: each row's proposal and resolution, read
          field-for-field from `FlaggedDifference` — the same data the trail's
          reconciliation-trace list already shows, here WITH the status and
          rationale as well as the name. */}
      {difference.proposedStatus !== undefined && (
        <p data-testid={`diff-proposal-${difference.diffId}`} className="mt-2 text-xs text-muted-foreground">
          {t('migration.reconciliation.proposalLine', {
            status: t(`migration.diffStatus.${difference.proposedStatus}`),
            name: difference.proposedByUserName ?? difference.proposedByUserId ?? '—',
            rationale: difference.proposedRationale ?? '—'
          })}
        </p>
      )}

      {disposed && (
        <p data-testid={`diff-resolution-${difference.diffId}`} className="mt-2 text-xs text-muted-foreground">
          {t('migration.reconciliation.resolutionLine', {
            status: t(`migration.diffStatus.${difference.status}`),
            name: difference.disposedByUserName ?? difference.disposedByUserId ?? '—',
            rationale: difference.disposedRationale ?? '—'
          })}
        </p>
      )}

      {scopedError !== undefined && (
        <p role="alert" data-testid={`diff-error-${difference.diffId}`} className="mt-2 text-xs text-blocked">
          {scopedError}
        </p>
      )}

      {!disposed && activeVerb === 'assign-owner' && (
        <form onSubmit={onAssignOwnerSubmit} className="mt-2 flex flex-wrap items-end gap-2">
          <div>
            <label htmlFor={`owner-${difference.diffId}`} className="text-xs font-medium text-foreground">
              {t('migration.reconciliation.ownerIdLabel')}
            </label>
            <MemberSelect
              id={`owner-${difference.diffId}`}
              members={members}
              value={ownerId}
              onChange={setOwnerId}
              required
              className="mt-1"
            />
          </div>
          <Button type="submit" size="sm" disabled={mutating}>
            {t('migration.reconciliation.submit')}
          </Button>
        </form>
      )}

      {!disposed && (activeVerb === 'propose' || activeVerb === 'dispose') && (
        <form
          onSubmit={activeVerb === 'propose' ? onProposeSubmit : onDisposeSubmit}
          className="mt-2 flex flex-wrap items-end gap-2"
        >
          <div>
            <label htmlFor={`disposition-${difference.diffId}`} className="text-xs font-medium text-foreground">
              {t('migration.reconciliation.dispositionLabel')}
            </label>
            <select
              id={`disposition-${difference.diffId}`}
              value={proposedStatus}
              onChange={(e) => setProposedStatus(e.target.value as FlaggedDifferenceStatus)}
              className="mt-1 h-9 rounded-md border border-input bg-card px-2 text-sm text-foreground"
            >
              {DISPOSITION_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {t(`migration.diffStatus.${option}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="min-w-[16rem] flex-1">
            <label htmlFor={`rationale-${difference.diffId}`} className="text-xs font-medium text-foreground">
              {t('migration.reconciliation.rationaleLabel')}
            </label>
            <Input
              id={`rationale-${difference.diffId}`}
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              required
              className="mt-1"
            />
          </div>
          <Button type="submit" size="sm" disabled={mutating}>
            {t('migration.reconciliation.submit')}
          </Button>
        </form>
      )}
    </div>
  )
}

/**
 * The reconciliation panel (AC-3, AC-4) — self-contained. The three
 * reconciliation verbs are three distinct call sites with three distinct
 * labels (INV-5.6); the fixture's single "accept the split" act does not
 * survive in any form.
 */
export function ReconciliationPanel({
  orgId,
  projectId,
  planId,
  runId
}: {
  orgId: string
  projectId: string
  planId: string | null
  runId: string | null
}) {
  const { t } = useTranslation('projectWorkspace')
  const dispatch = useAppDispatch()
  const report = useAppSelector(selectReconciliationReport)
  const loading = useAppSelector(selectReconciliationLoading)
  const error = useAppSelector(selectReconciliationError)
  const mutating = useAppSelector(selectMigrationMutating)
  const nonce = useAppSelector(selectMigrationNonce)
  const members = useAppSelector(selectProjectMembers)
  const [active, setActive] = useState<ActiveAction>(null)

  useEffect(() => {
    if (planId === null || runId === null) return
    void dispatch(loadReconciliation({ orgId, projectId, planId, runId }))
  }, [dispatch, orgId, projectId, planId, runId, nonce])

  const onRetryClicked = useCallback(() => {
    if (planId === null || runId === null) return
    void dispatch(loadReconciliation({ orgId, projectId, planId, runId }))
  }, [dispatch, orgId, projectId, planId, runId])

  if (planId === null || runId === null) {
    return (
      <section aria-label={t('migration.reconciliation.title')} className="rounded-lg border border-border bg-card px-4 py-3.5">
        <SectionLabel>{t('migration.reconciliation.title')}</SectionLabel>
        <div className="mt-2">
          <EmptyPanel title={t('migration.reconciliation.noRunTitle')} body={t('migration.reconciliation.noRunBody')} />
        </div>
      </section>
    )
  }

  const rows = report?.rows ?? []
  const pageError = error !== null && error.scope === undefined ? error : null

  return (
    <section
      aria-label={t('migration.reconciliation.title')}
      className="rounded-lg border border-border bg-card px-4 py-3.5"
      data-testid="migration-reconciliation-panel"
    >
      <div className="flex flex-wrap items-center gap-2">
        <SectionLabel>{t('migration.reconciliation.title')}</SectionLabel>
        {report !== null && (
          <StatusPill tone="neutral">{t(`migration.reportStatus.${report.status}`)}</StatusPill>
        )}
      </div>

      {pageError !== null ? (
        <div className="mt-2">
          <ErrorPanel error={toDisplayError(pageError)} onRetryClicked={onRetryClicked} />
        </div>
      ) : loading && report === null ? (
        <div className="mt-2">
          <LoadingPanel label={t('migration.reconciliation.loading')} />
        </div>
      ) : rows.length === 0 ? (
        <div className="mt-2">
          <EmptyPanel title={t('migration.reconciliation.emptyTitle')} body={t('migration.reconciliation.emptyBody')} />
        </div>
      ) : (
        <ul className="mt-2 overflow-hidden rounded-lg border border-border">
          {rows.map((row) => {
            const scopedMessage =
              error !== null && error.scope === row.flaggedDifference?.diffId ? error.message : undefined
            return (
              <li key={row.objectKey}>
                <div className="v-row flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5">
                  <StatusPill tone={ROW_TONE[row.status]}>{t(`migration.rowStatus.${row.status}`)}</StatusPill>
                  <span className="font-mono text-[13px] text-foreground">{row.objectKey}</span>
                  {row.flaggedDifference !== undefined && (
                    <>
                      <StatusPill tone="neutral">
                        {t(`migration.diffKind.${row.flaggedDifference.kind}`)}
                      </StatusPill>
                      <StatusPill tone={DIFF_STATUS_TONE[row.flaggedDifference.status]}>
                        {t(`migration.diffStatus.${row.flaggedDifference.status}`)}
                      </StatusPill>
                    </>
                  )}
                  <span className="ml-auto text-xs text-muted-foreground">
                    {t('migration.reconciliation.counts', {
                      source: row.sourceCount ?? '—',
                      target: row.targetCount ?? '—'
                    })}
                  </span>
                </div>
                {row.status === 'DIFFERENCE' && (
                  <DifferenceActions
                    orgId={orgId}
                    projectId={projectId}
                    planId={planId}
                    runId={runId}
                    row={row}
                    active={active}
                    onActiveChanged={setActive}
                    mutating={mutating === row.flaggedDifference?.diffId}
                    scopedError={scopedMessage}
                    members={members}
                  />
                )}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
